/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */


(() => {
  const selector = '[data-ab-b2b-price]';
  const publicProducts = new Map();
  const pending = new WeakSet();
  const english = document.documentElement.lang.toLowerCase().startsWith('en');
  const labels = english ? {list: 'List price', yours: 'Your price', from: 'from '} : {list: 'Listenpreis', yours: 'Ihr Preis', from: 'ab '};
  const locale = document.documentElement.lang || 'de-DE';
  let scheduled = false;
  const money = (cents, currency) => new Intl.NumberFormat(locale, {style: 'currency', currency}).format(cents / 100);
  const setText = (node, value) => { if (node.textContent !== value) node.textContent = value; };

  async function loadPublicProduct(handle) {
    let after = null;
    const variants = [];
    let product;
    do {
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          query: 'query ListPrice($handle:String!,$after:String){product(handle:$handle){priceRange{minVariantPrice{amount currencyCode} maxVariantPrice{amount}} variants(first:100,after:$after){nodes{id price{amount currencyCode}} pageInfo{hasNextPage endCursor}}}}',
          variables: {handle, after}
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(JSON.stringify(result.errors || result).slice(0,500));
      product = result.data?.product;
      if (result.errors?.length || !product) throw new Error('Public product unavailable');
      variants.push(...product.variants.nodes.map(variant => ({
        id: variant.id.split('/').pop(),
        price: Math.round(Number(variant.price.amount) * 100)
      })));
      after = product.variants.pageInfo.hasNextPage ? product.variants.pageInfo.endCursor : null;
    } while (after);
    return {
      variants,
      currency: product.priceRange.minVariantPrice.currencyCode,
      price_varies: product.priceRange.minVariantPrice.amount !== product.priceRange.maxVariantPrice.amount
    };
  }
  function getPublicProduct(block) {
    const handle = block.dataset.abB2bHandle;
    if (!publicProducts.has(handle)) publicProducts.set(handle, loadPublicProduct(handle).catch(() => null));
    return publicProducts.get(handle);
  }

  function removeListRow(block) {
    const row = block.dataset.abB2bPrice === 'variant'
      ? block.closest('.product-form__info-item')?.previousElementSibling
      : block.querySelector('.ab-b2b-list-row');
    if (row?.classList.contains('ab-b2b-list-row')) row.remove();
  }

  async function update(block) {
    if (!block.isConnected || pending.has(block)) return;
    const currency = block.dataset.abB2bCurrency;
    if (currency !== block.dataset.abB2bBaseCurrency) return;
    const mode = block.dataset.abB2bPrice;
    const isVariant = mode === 'variant';
    const isCart = mode === 'cart-unit' || mode === 'cart-total';
    if (!isVariant && !block.querySelector('.ab-b2b-your-label')) {
      const label = document.createElement('span');
      label.className = 'ab-b2b-your-label';
      label.textContent = labels.yours + ':';
      block.insertBefore(label, block.firstChild);
    }
    pending.add(block);
    try {
      const product = await getPublicProduct(block);
      if (!product || !block.isConnected || product.currency !== currency) return;
      let listPrice, currentPrice, varies = false;
      if (isCart) {
        const variant = product.variants.find(item => String(item.id) === block.dataset.abB2bVariant);
        listPrice = variant?.price * Number(block.dataset.abB2bQuantity || 1);
        currentPrice = Number(block.dataset.abB2bCurrent);
      } else if (isVariant) {
        const form = block.closest('form');
        const variantId = form?.querySelector('[name="id"]')?.value || block.dataset.abB2bVariant;
        const variant = product.variants.find(item => String(item.id) === String(variantId));
        const state = form?.querySelector('script[data-variant]');
        let activeVariant = null;
        try { activeVariant = state ? JSON.parse(state.textContent) : null; } catch (_) {}
        listPrice = variant?.price;
        currentPrice = activeVariant && String(activeVariant.id) === String(variantId)
          ? activeVariant.price : Number(block.dataset.abB2bCurrent);
      } else {
        listPrice = Math.min(...product.variants.map(variant => variant.price));
        currentPrice = Number(block.dataset.abB2bCurrent);
        varies = product.price_varies;
      }
      if (!Number.isFinite(listPrice) || !Number.isFinite(currentPrice) || listPrice <= currentPrice) {
        removeListRow(block);
        return;
      }
      let row;
      if (isVariant) {
        const item = block.closest('.product-form__info-item');
        row = item.previousElementSibling;
        if (!row?.classList.contains('ab-b2b-list-row')) {
          row = document.createElement('div');
          row.className = 'product-form__info-item ab-b2b-list-row';
          const label = document.createElement('span');
          label.className = 'product-form__info-title text--strong';
          label.textContent = labels.list + ':';
          const content = document.createElement('div');
          content.className = 'product-form__info-content';
          content.appendChild(document.createElement('s'));
          row.append(label, content);
          item.before(row);
        }
      } else {
        row = block.querySelector('.ab-b2b-list-row');
        if (!row) {
          row = document.createElement('div');
          row.className = 'ab-b2b-list-row';
          const label = document.createElement('span');
          label.textContent = (mode === 'cart-unit' ? (english ? 'List price / unit' : 'Listenpreis / Stück') : labels.list) + ': ';
          row.append(label, document.createElement('s'));
          block.insertBefore(row, block.firstChild);
        }
      }
      setText(row.querySelector('s'), (varies ? labels.from : '') + money(listPrice, currency));
      block.dataset.abB2bLoaded = 'true';
    } finally {
      pending.delete(block);
    }
  }

  function scan() {
    scheduled = false;
    document.querySelectorAll(selector).forEach(update);
  }
  function schedule() {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(scan);
    }
  }
  function init() {
    const style = document.createElement('style');
    style.textContent = '[data-ab-b2b-price] .price--compare{display:none}[data-ab-b2b-price] .price{color:#002891;font-weight:600}.ab-b2b-list-row{color:#64748b;font-size:14px;line-height:1.5}.ab-b2b-list-row s{white-space:nowrap}.product-form__info-item.ab-b2b-list-row{margin-bottom:6px}.product-form__info-item.ab-b2b-list-row .product-form__info-title{font-weight:400;color:#64748b}[data-ab-b2b-price="range"]{display:flex;flex-direction:column;align-items:flex-start;gap:2px}[data-ab-b2b-price="range"] .ab-b2b-your-label{font-size:12px;font-weight:600;color:#002891}';
    style.textContent += "[data-ab-b2b-price^=\"cart-\"]{display:flex;flex-direction:column;gap:2px}[data-ab-b2b-price=\"cart-unit\"]{align-items:flex-start}[data-ab-b2b-price=\"cart-total\"]{align-items:flex-end;white-space:normal}[data-ab-b2b-price^=\"cart-\"] .line-item__price--compare{display:none}[data-ab-b2b-price^=\"cart-\"] .ab-b2b-your-label{font-size:12px;line-height:1.4;color:#64748b}[data-ab-b2b-price^=\"cart-\"] .line-item__price,[data-ab-b2b-price^=\"cart-\"] .ab-b2b-cart-current{color:#002891;font-weight:600}[data-ab-b2b-price=\"cart-total\"] .ab-b2b-cart-current{font-size:16px}.ab-b2b-cart-vat{font-size:12px;line-height:1.5;margin:4px 0;color:#64748b}.ab-b2b-mobile-total{margin:10px 0}.ab-b2b-mobile-total [data-ab-b2b-price=\"cart-total\"]{align-items:flex-start}.line-item__line-price .ab-b2b-list-row{font-size:12px;line-height:1.5}";
    document.head.appendChild(style);
    new MutationObserver(schedule).observe(document.body, {childList: true, subtree: true, characterData: true});
    document.addEventListener('variant:changed', schedule);
    document.addEventListener('shopify:section:load', schedule);
    scan();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
